import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditTrailModal = ({ isOpen, onClose, shipment }) => {
  if (!isOpen || !shipment) return null;

  const auditHistory = [
    {
      id: 1,
      action: 'Entry Created',
      user: 'Sarah Chen',
      timestamp: '2026-01-20T14:23:15',
      details: 'Initial tracking number entry',
      icon: 'Plus',
      color: 'success'
    },
    {
      id: 2,
      action: 'Status Updated',
      user: 'Mike Johnson',
      timestamp: '2026-01-20T15:45:30',
      details: 'Changed status from Pending to In Transit',
      icon: 'Edit',
      color: 'warning'
    },
    {
      id: 3,
      action: 'Information Modified',
      user: 'Sarah Chen',
      timestamp: '2026-01-20T16:12:45',
      details: 'Updated customer name and destination address',
      icon: 'FileEdit',
      color: 'primary'
    },
    {
      id: 4,
      action: 'Status Updated',
      user: 'System',
      timestamp: '2026-01-21T09:30:00',
      details: 'Automatically updated to Delivered based on carrier API',
      icon: 'CheckCircle2',
      color: 'success'
    }
  ];

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date?.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      }),
      time: date?.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      })
    };
  };

  return (
    <>
      <div 
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-300"
        onClick={onClose}
        aria-hidden="true"
      />
      <div 
        className="fixed inset-0 z-300 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-trail-title"
      >
        <div className="flex min-h-full items-center justify-center p-24">
          <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-[700px] animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-32 py-24 border-b border-border">
              <div>
                <h2 
                  id="audit-trail-title"
                  className="font-heading font-semibold text-h4 text-foreground mb-4"
                >
                  Audit Trail
                </h2>
                <p className="font-body text-[14px] text-muted-foreground">
                  Complete modification history for tracking number
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-[40px] h-[40px] flex items-center justify-center rounded-lg hover:bg-muted transition-smooth"
                aria-label="Close audit trail"
              >
                <Icon name="X" size={20} color="var(--color-foreground)" strokeWidth={2} />
              </button>
            </div>

            <div className="px-32 py-24">
              <div className="bg-muted rounded-lg p-18 mb-24">
                <div className="grid grid-cols-2 gap-18">
                  <div>
                    <div className="font-caption text-[11px] text-muted-foreground uppercase tracking-wide mb-4">
                      Tracking Number
                    </div>
                    <div className="font-mono text-[14px] text-foreground font-medium">
                      {shipment?.tracking}
                    </div>
                  </div>
                  <div>
                    <div className="font-caption text-[11px] text-muted-foreground uppercase tracking-wide mb-4">
                      Current Status
                    </div>
                    <div className="font-body text-[14px] text-foreground font-medium">
                      {shipment?.status}
                    </div>
                  </div>
                  <div>
                    <div className="font-caption text-[11px] text-muted-foreground uppercase tracking-wide mb-4">
                      Carrier
                    </div>
                    <div className="font-body text-[14px] text-foreground">
                      {shipment?.carrier}
                    </div>
                  </div>
                  <div>
                    <div className="font-caption text-[11px] text-muted-foreground uppercase tracking-wide mb-4">
                      Ship Date
                    </div>
                    <div className="font-body text-[14px] text-foreground">
                      {new Date(shipment.shipDate)?.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-18 max-h-[400px] overflow-y-auto pr-12">
                {auditHistory?.map((entry, index) => {
                  const { date, time } = formatDateTime(entry?.timestamp);
                  return (
                    <div 
                      key={entry?.id}
                      className="relative pl-32 pb-18"
                    >
                      {index !== auditHistory?.length - 1 && (
                        <div className="absolute left-[15px] top-[32px] bottom-0 w-[2px] bg-border" />
                      )}
                      <div className="absolute left-0 top-0 w-[32px] h-[32px] rounded-full bg-card border-2 border-border flex items-center justify-center">
                        <Icon 
                          name={entry?.icon} 
                          size={16} 
                          color={`var(--color-${entry?.color})`}
                          strokeWidth={2} 
                        />
                      </div>
                      <div className="bg-muted rounded-lg p-18">
                        <div className="flex items-start justify-between mb-8">
                          <div>
                            <h3 className="font-body font-semibold text-[15px] text-foreground mb-2">
                              {entry?.action}
                            </h3>
                            <p className="font-body text-[13px] text-muted-foreground">
                              {entry?.details}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-18 mt-12 pt-12 border-t border-border">
                          <div className="flex items-center gap-8">
                            <Icon 
                              name="User" 
                              size={14} 
                              color="var(--color-muted-foreground)" 
                              strokeWidth={2} 
                            />
                            <span className="font-caption text-[12px] text-muted-foreground">
                              {entry?.user}
                            </span>
                          </div>
                          <div className="flex items-center gap-8">
                            <Icon 
                              name="Calendar" 
                              size={14} 
                              color="var(--color-muted-foreground)" 
                              strokeWidth={2} 
                            />
                            <span className="font-caption text-[12px] text-muted-foreground">
                              {date}
                            </span>
                          </div>
                          <div className="flex items-center gap-8">
                            <Icon 
                              name="Clock" 
                              size={14} 
                              color="var(--color-muted-foreground)" 
                              strokeWidth={2} 
                            />
                            <span className="font-caption text-[12px] text-muted-foreground">
                              {time}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-end gap-12 px-32 py-24 border-t border-border">
              <Button
                variant="outline"
                onClick={onClose}
              >
                Close
              </Button>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
              >
                Export History
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuditTrailModal;